import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getUserSubscriptions,
    getBookShelfByUser,
    getSubscriptionProducts,
    getSubscriptionById,
    getMemberById,
    requestRefund,
} from "../../../api/productApi";
import {
    formatCharges,
    formatAddress,
    getMemberName,
    getDurationLabel,
} from "../utils";

const parseAmount = (val) => {
    if (val === null || val === undefined) return 0;
    const str = String(val).replace(/[^0-9.]/g, "");
    const parsed = parseFloat(str);
    return isNaN(parsed) ? 0 : parsed;
};

export default function useManageSubscription() {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const navigate = useNavigate();
    const location = useLocation();

    const passedMember = location.state?.member || null;

    // States
    const [selectedMember, setSelectedMember] = useState(passedMember);
    const [memberCredits, setMemberCredits] = useState(null);
    const [fullMemberInfo, setFullMemberInfo] = useState(null);
    const [loadingMemberInfo, setLoadingMemberInfo] = useState(false);
    const [memberInfoError, setMemberInfoError] = useState("");

    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [refunding, setRefunding] = useState(false);
    const [refundSuccessMsg, setRefundSuccessMsg] = useState("");
    const [paymentCollecting, setPaymentCollecting] = useState(false);
    const [paymentSuccessMsg, setPaymentSuccessMsg] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentAmount, setPaymentAmount] = useState("");
    const [requestingRefund, setRequestingRefund] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => {
            setToast(null);
        }, 4000);
    }, []);

    const [subscriptions, setSubscriptions] = useState([]);
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [subsError, setSubsError] = useState("");

    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [loadingSubDetails, setLoadingSubDetails] = useState(false);
    const [subDetailsError, setSubDetailsError] = useState("");

    const [query, setQuery] = useState("");
    const [subscriptionProducts, setSubscriptionProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productsError, setProductsError] = useState("");

    const [cart, setCart] = useState([]);

    // API Handlers
    const fetchSubscriptionProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            setProductsError("");
            const res = await getSubscriptionProducts({ token });
            if (!res?.success) {
                setSubscriptionProducts([]);
                setProductsError("No subscription products found");
                return;
            }
            setSubscriptionProducts(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            setSubscriptionProducts([]);
            setProductsError("Failed to load subscription products");
        } finally {
            setLoadingProducts(false);
        }
    }, [token]);

    const fetchUserSubscriptions = useCallback(async (userId) => {
        if (!userId) return;
        try {
            setLoadingSubs(true);
            setSubsError("");
            setSelectedSubscriptionId(null);
            setSubscriptionDetails(null);
            setSubDetailsError("");

            const res = await getUserSubscriptions({ token, userId });
            if (!res?.success) {
                setSubscriptions([]);
                setSubsError("No subscriptions found");
                return;
            }
            setSubscriptions(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            setSubscriptions([]);
            setSubsError("Failed to load subscriptions");
        } finally {
            setLoadingSubs(false);
        }
    }, [token]);

    const fetchMemberInfo = useCallback(async (userId) => {
        if (!userId) return;
        try {
            setLoadingMemberInfo(true);
            setMemberInfoError("");

            const [shelfRes, infoRes] = await Promise.all([
                getBookShelfByUser({ userId, token }),
                getMemberById({ token, userId })
            ]);

            const credits = shelfRes?.data?.credits ?? null;
            setMemberCredits(credits);

            if (infoRes?.success && infoRes?.data?.member_information) {
                const info = infoRes.data.member_information;
                setFullMemberInfo({
                    id: userId,
                    name: info.member_name,
                    phone: info.phone_number,
                    email: info.email,
                    dob: info.date_of_birth,
                    subscription: info.subscription_plan,
                    deposit: info.deposit,
                    damageCharges: info.damage_charges,
                    overdueCharges: info.overdue_charges,
                    billingAddress: info.billing_address,
                    credits: credits ?? infoRes?.data?.credits ?? 0,
                });
            }
        } catch (err) {
            setMemberCredits(null);
            setFullMemberInfo(null);
            setMemberInfoError("Failed to load member information");
        } finally {
            setLoadingMemberInfo(false);
        }
    }, [token]);

    const fetchSubscriptionDetailsById = useCallback(async (subscriptionId) => {
        if (!subscriptionId) return;
        try {
            setLoadingSubDetails(true);
            setSubDetailsError("");
            setSubscriptionDetails(null);

            const res = await getSubscriptionById({ token, subscriptionId });
            if (!res?.success) {
                setSubscriptionDetails(null);
                setSubDetailsError("Subscription details not found");
                return;
            }
            setSubscriptionDetails(res?.data || null);
        } catch (err) {
            setSubscriptionDetails(null);
            setSubDetailsError("Failed to load subscription details");
        } finally {
            setLoadingSubDetails(false);
        }
    }, [token]);

    // Initial Load & State Sync
    useEffect(() => {
        fetchSubscriptionProducts();
    }, [fetchSubscriptionProducts]);

    useEffect(() => {
        if (passedMember?.id) {
            setSelectedMember(passedMember);
            fetchUserSubscriptions(passedMember.id);
            fetchMemberInfo(passedMember.id);
        }
    }, [passedMember, fetchUserSubscriptions, fetchMemberInfo]);

    // Navigation Helper
    const handleGoBack = () => {
        if (selectedMember?.id) {
            navigate("/members", { state: { memberId: selectedMember.id } });
        } else {
            navigate(-1);
        }
    };

    // Derived Data
    const filteredProducts = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return subscriptionProducts;
        return subscriptionProducts.filter((p) =>
            String(p?.name || "").toLowerCase().includes(q)
        );
    }, [subscriptionProducts, query]);

    const selectedPlanName = subscriptions?.[0]?.items?.[0]?.name || "-";

    const memberData = useMemo(() => {
        if (!fullMemberInfo) {
            return {
                name: getMemberName(selectedMember),
                phone: selectedMember?.phone || "-",
                clientId: selectedMember?.id || "-",
                email: selectedMember?.email || "-",
                plan: "-",
                credits: "-",
                address: selectedMember?.billing_address || "-",
                deposit: 0,
                damageCharges: 0,
                overdueCharges: 0,
            };
        }
        return {
            name: fullMemberInfo.name || "-",
            phone: fullMemberInfo.phone || "-",
            clientId: selectedMember?.id || "-",
            email: fullMemberInfo.email || "-",
            plan: fullMemberInfo.subscription || selectedPlanName,
            credits: fullMemberInfo.credits ?? "-",
            address: typeof fullMemberInfo.billingAddress === "object"
                ? formatAddress(fullMemberInfo.billingAddress)
                : fullMemberInfo.billingAddress || "-",
            deposit: fullMemberInfo.deposit || 0,
            damageCharges: formatCharges(fullMemberInfo.damageCharges),
            overdueCharges: formatCharges(fullMemberInfo.overdueCharges),
        };
    }, [fullMemberInfo, selectedMember, selectedPlanName]);

    // Refund Calculations
    const depositAmount = Number(memberData.deposit || 0);
    const overdueCharges = Number(memberData.overdueCharges || 0);
    const damageCharges = Number(memberData.damageCharges || 0);
    const finalRefund = depositAmount - overdueCharges - damageCharges;

    const showRefundButton = useMemo(() => {
        if (!subscriptionDetails) return false;

        const details = subscriptionDetails.subscription_details;
        const depositDamage = subscriptionDetails.deposit_and_damage;

        const remaining_amount = depositDamage?.remaining_amount ?? subscriptionDetails?.remaining_amount ?? 0;
        const subscription_status = details?.status ?? details?.subscription_status ?? subscriptionDetails?.subscription_status ?? "";

        return Number(parseAmount(remaining_amount)) > 0 && subscription_status === "on-hold";
    }, [subscriptionDetails]);

    // Cart Actions
    const addToCart = (product) => {
        const item = {
            id: String(product.product_id),
            productId: product.product_id,
            title: product.name,
            price: `₹${product.price_formatted || product.price || "-"}`,
            duration: getDurationLabel(product.billing_interval, product.billing_period),
        };
        setCart([item]);
    };

    const removeFromCart = (id) => {
        setCart((prev) => prev.filter((x) => x.id !== id));
    };

    const handleViewSubscription = (sub) => {
        const subId = sub?.subscription_id;
        if (!subId) return;

        setSelectedSubscriptionId(subId);
        fetchSubscriptionDetailsById(subId);

        setTimeout(() => {
            const el = document.getElementById("subscription-details-section");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };


    const handleRequestRefund = async () => {
        const details = subscriptionDetails?.subscription_details;
        const depositDamage = subscriptionDetails?.deposit_and_damage;
        const orderId = details?.id;
        const remainingAmt = depositDamage?.remaining_amount;

        if (!orderId) {
            showToast("Subscription Order ID is missing.", "error");
            return;
        }

        if (!showRefundButton) {
            showToast("Refund conditions are not met.", "error");
            return;
        }

        setRequestingRefund(true);
        try {
            const payload = {
                order_id: Number(orderId),
                reason: "User requested refund for unused remaining deposit",
                amount: parseAmount(remainingAmt),
            };

            const res = await requestRefund({ token, payload });
            if (res?.success) {
                showToast(res?.message || "Refund requested successfully!", "success");

                // Refresh Deposit And Damage History
                await fetchSubscriptionDetailsById(orderId);

                // Refresh refund status / member info if applicable
                if (selectedMember?.id) {
                    await fetchMemberInfo(selectedMember.id);
                }
            } else {
                const errMsg = res?.message || "Failed to request refund.";
                showToast(errMsg, "error");
            }
        } catch (err) {
            const errMsg = err.message || "An error occurred while requesting refund.";
            showToast(errMsg, "error");
        } finally {
            setRequestingRefund(false);
        }
    };

    // Settlement Actions
    const handleProcessRefund = async () => {
        const details = subscriptionDetails?.subscription_details;
        const depositDamage = subscriptionDetails?.deposit_and_damage;
        const orderId = details?.id;
        const remainingAmt = depositDamage?.remaining_amount;

        if (!orderId) {
            showToast("Subscription Order ID is missing.", "error");
            return;
        }

        if (!showRefundButton) {
            showToast("Refund conditions are not met.", "error");
            return;
        }

        setRefunding(true);
        try {
            const payload = {
                order_id: Number(orderId),
                reason: "User requested refund for unused remaining deposit",
                amount: parseAmount(remainingAmt),
            };

            const res = await requestRefund({ token, payload });
            if (res?.success) {
                setRefundSuccessMsg(res?.message || `Refund of ₹${parseAmount(remainingAmt).toFixed(2)} processed successfully!`);
                showToast(res?.message || "Refund processed successfully!", "success");

                // Refresh Deposit And Damage History
                await fetchSubscriptionDetailsById(orderId);

                // Refresh refund status / member info
                if (selectedMember?.id) {
                    await fetchMemberInfo(selectedMember.id);
                }
            } else {
                const errMsg = res?.message || "Failed to process refund.";
                showToast(errMsg, "error");
            }
        } catch (err) {
            const errMsg = err.message || "An error occurred while processing refund.";
            showToast(errMsg, "error");
        } finally {
            setRefunding(false);
        }
    };

    // TODO: Integrate actual payment collection API when available.
    // Currently, there is no payment collection endpoint in productApi.
    const handleCollectPayment = () => {
        const amt = parseFloat(paymentAmount);
        if (isNaN(amt) || amt <= 0) return;

        setPaymentCollecting(true);
        try {
            setPaymentSuccessMsg(`Payment of ₹${amt.toFixed(2)} collected via ${paymentMethod} successfully! Dues settled.`);

            // Settle overdue and damage charges
            if (subscriptionDetails) {
                setSubscriptionDetails((prev) => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        deposit_and_damage: {
                            ...prev.deposit_and_damage,
                            damage_charges: [],
                            remaining_amount: Number(prev.deposit_and_damage?.deposit_amount || 0)
                        }
                    };
                });
            }
            if (fullMemberInfo) {
                setFullMemberInfo((prev) => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        damageCharges: [],
                        overdueCharges: 0
                    };
                });
            }
        } finally {
            setPaymentCollecting(false);
        }
    };

    const handleOpenRefundModal = () => {
        setRefundSuccessMsg("");
        setPaymentSuccessMsg("");
        setPaymentAmount("");
        setIsRefundModalOpen(true);
    };

    return {
        // State
        selectedMember,
        memberCredits,
        fullMemberInfo,
        loadingMemberInfo,
        memberInfoError,
        isRefundModalOpen,
        setIsRefundModalOpen,
        refunding,
        refundSuccessMsg,
        paymentCollecting,
        paymentSuccessMsg,
        paymentMethod,
        setPaymentMethod,
        paymentAmount,
        setPaymentAmount,
        subscriptions,
        loadingSubs,
        subsError,
        selectedSubscriptionId,
        subscriptionDetails,
        loadingSubDetails,
        subDetailsError,
        query,
        setQuery,
        cart,
        loadingProducts,
        productsError,
        requestingRefund,
        toast,
        showRefundButton,

        // Actions
        handleGoBack,
        addToCart,
        removeFromCart,
        handleViewSubscription,
        handleRequestRefund,
        handleProcessRefund,
        handleCollectPayment,
        handleOpenRefundModal,

        // Derived / Calculations
        filteredProducts,
        memberData,
        depositAmount,
        overdueCharges,
        damageCharges,
        finalRefund,
    };
}
